import { z } from 'zod'

/**
 * cogo.verified-design-libraries.manifest.v1 — the CLIENT-READABLE projection
 * of the figma-sources admission evidence (docs/figma-sources/*).
 *
 * The manifest is GENERATED, never hand-edited: the pure builder in
 * ./build-manifest.ts derives every field from the committed vault registry
 * (registry.v1.json), the per-source receipts (receipts/<sourceId>/...), and
 * the admission-v2 gate records (admission-v2/<sourceId>/...). The repo-root
 * CLI `scripts/build-verified-design-libraries-manifest.ts` writes it to
 * docs/figma-sources/verified-design-libraries.manifest.v1.json, and the
 * sync test in __tests__/manifest-sync.test.ts rebuilds it from the sources
 * on every unit run — drift between receipts and manifest is a red test, not
 * a silently stale screen.
 *
 * Everything here is `.strict()`: an unknown key means the generator and the
 * client disagree about the schema, and the client's answer to that is to
 * fail closed (see read-verified-design-libraries.ts), not to render a
 * half-understood registry.
 */
export const VERIFIED_DESIGN_LIBRARIES_MANIFEST_SCHEMA_VERSION =
  'cogo.verified-design-libraries.manifest.v1' as const

/**
 * The six library-harness capability axes, exactly as they appear in
 * `cogo.library-reuse-admission-receipt.v2` `grant` blocks and in
 * admission-v2 gate reports' `effectiveCapabilities`. Admission of any of
 * these is NOT generation eligibility — that is a separate, per-source
 * `generationEligible` fact from the vault registry.
 */
export const capabilityGrantSchema = z
  .object({
    localEvaluation: z.boolean(),
    ontologyExtraction: z.boolean(),
    packDerivation: z.boolean(),
    derivativePublication: z.boolean(),
    rawRedistribution: z.boolean(),
    upstreamWriteback: z.boolean(),
  })
  .strict()

export type CapabilityGrant = z.infer<typeof capabilityGrantSchema>

export const licenseFactSchema = z
  .object({
    status: z.string().min(1),
    spdx: z.string().min(1),
  })
  .strict()

export type LicenseFact = z.infer<typeof licenseFactSchema>

/** A receipt reference: identity + where the full evidence lives in-repo. */
export const receiptRefSchema = z
  .object({
    receiptId: z.string().min(1),
    schemaVersion: z.string().min(1),
    path: z.string().min(1),
  })
  .strict()

export type ReceiptRef = z.infer<typeof receiptRefSchema>

export const reuseReceiptSchema = receiptRefSchema
  .extend({
    grant: capabilityGrantSchema,
    license: licenseFactSchema.optional(),
  })
  .strict()

export type ReuseReceipt = z.infer<typeof reuseReceiptSchema>

export const localEvaluationRightsSchema = z
  .object({
    receiptId: z.string().min(1),
    schemaVersion: z.string().min(1),
    path: z.string().min(1),
    permittedOperations: z.array(z.string().min(1)),
    assetEgressAllowed: z.boolean(),
    networkExfiltrationAllowed: z.boolean(),
    license: licenseFactSchema.optional(),
  })
  .strict()

export type LocalEvaluationRights = z.infer<typeof localEvaluationRightsSchema>

/**
 * Human confirmation decisions carry WHO approved in the source receipt —
 * deliberately NOT projected here: the manifest ships inside the product
 * bundle, and the decision's identity (an operator e-mail) is provenance a
 * reviewer reads in-repo, not data the client needs.
 */
export const confirmationDecisionSchema = z
  .object({
    decisionId: z.string().min(1),
    decision: z.string().min(1),
    decidedAt: z.string().min(1),
    path: z.string().min(1),
  })
  .strict()

export type ConfirmationDecision = z.infer<typeof confirmationDecisionSchema>

export const admissionGateReportSchema = z
  .object({
    path: z.string().min(1),
    status: z.string().min(1),
    nextRequiredGate: z.string().min(1).optional(),
    effectiveCapabilities: capabilityGrantSchema.optional(),
  })
  .strict()

export type AdmissionGateReport = z.infer<typeof admissionGateReportSchema>

export const designationRefSchema = z
  .object({
    designationId: z.string().min(1),
    path: z.string().min(1),
  })
  .strict()

export type DesignationRef = z.infer<typeof designationRefSchema>

/**
 * The evidence-derived admission stage. Derivation is mechanical (see
 * build-manifest.ts): a stage is claimed only when the files that prove it
 * exist, and 'library-reuse-admitted' still does not mean generation —
 * `agreedGrant` + `generationEligible` carry the actual scope.
 */
export const admissionStageSchema = z.enum([
  'library-reuse-admitted',
  'admission-blocked',
  'designated',
  'no-admission-evidence',
])

export type AdmissionStage = z.infer<typeof admissionStageSchema>

export const libraryAdmissionSchema = z
  .object({
    stage: admissionStageSchema,
    blockedAtGate: z.string().min(1).optional(),
    designations: z.array(designationRefSchema),
    gateReport: admissionGateReportSchema.optional(),
    qualityAdmissionReceipts: z.array(receiptRefSchema),
    reuseReceipts: z.array(reuseReceiptSchema),
    localEvaluationRights: z.array(localEvaluationRightsSchema),
    confirmationDecisions: z.array(confirmationDecisionSchema),
    /**
     * The grant every reuse receipt agrees on, or null — null both when there
     * are no reuse receipts and when per-selection receipts disagree (the UI
     * must then say "varies by receipt", never pick a winner).
     */
    agreedGrant: capabilityGrantSchema.nullable(),
    agreedLicense: licenseFactSchema.nullable(),
  })
  .strict()

export type LibraryAdmission = z.infer<typeof libraryAdmissionSchema>

/** Snapshot facts pass through the vault registry's own status strings verbatim. */
export const snapshotFactSchema = z
  .object({
    status: z.string().min(1),
    sha256: z.string().min(1).optional(),
    byteLength: z.number().int().nonnegative().optional(),
    importReceiptPath: z.string().min(1).optional(),
  })
  .strict()

export type SnapshotFact = z.infer<typeof snapshotFactSchema>

export const verifiedDesignLibrarySchema = z
  .object({
    sourceId: z.string().min(1),
    sourceKind: z.string().min(1),
    rightsRoute: z.string().min(1),
    generationEligible: z.boolean(),
    communityResourceId: z.string().min(1).optional(),
    communityUrl: z.string().min(1).optional(),
    snapshot: snapshotFactSchema,
    admission: libraryAdmissionSchema,
  })
  .strict()

export type VerifiedDesignLibrary = z.infer<typeof verifiedDesignLibrarySchema>

export const verifiedDesignLibrariesManifestSchema = z
  .object({
    schemaVersion: z.literal(VERIFIED_DESIGN_LIBRARIES_MANIFEST_SCHEMA_VERSION),
    generatedBy: z.literal('scripts/build-verified-design-libraries-manifest.ts'),
    generatedFrom: z
      .object({
        vaultRegistry: z.literal('docs/figma-sources/registry.v1.json'),
        receiptsRoot: z.literal('docs/figma-sources/receipts'),
        admissionRoot: z.literal('docs/figma-sources/admission-v2'),
      })
      .strict(),
    libraries: z.array(verifiedDesignLibrarySchema),
  })
  .strict()

export type VerifiedDesignLibrariesManifest = z.infer<
  typeof verifiedDesignLibrariesManifestSchema
>
